import React, { useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useFormik } from "formik";
import { Button } from "../../common/Button";
import { failed, success } from "../../common/Toastify";
import TextInput from "../../common/textfield/TextInput";
import TextArea from "../../common/textfield/TextArea";
import { FormikObj } from "./constant.js";
import { Switch } from "@mui/material";
import { addDocumentReference } from "../../api/FillableDocument.js";
import { axios } from "../../../lib/axios";
import { useSelector } from "react-redux";
import Select from "react-select";
import { getOrganization } from "../../api/Organization/index.js";
import { useClinicLocationsQuery } from "../../../hooks/ReactQueryHooks/useClinicLocationsQuery.js";

export function CreateResource({ modalShow, handleShow, refreshTable }) {
  const [btnLoading, setBtnLoading] = useState(false);
  const user = useSelector((state) => state?.auth?.user)
  const practitionerId = user?.["custom:practitioner_id"];
  const practitionerName = user?.name?.[0]?.text;

  const [locations, setLocations] = useState([]);
  const organizations = useRef([]);

  const fetchOrganizations = async () => {
    const response = await getOrganization();
    organizations.current = response?.data || [];
  };

  useEffect(() => { fetchOrganizations(); }, []);

  const formik = useFormik({
    ...FormikObj,
    onSubmit: async (values) => {
      const selectedLocations = values.locations.map((loc) => ({
        reference: `Location/${loc.value}`,
        display: loc.label,
      }));

      const author = [{
        display: practitionerName,
        reference: `Practitioner/${practitionerId}`,
        type: "Practitioner",
      }];

      const { organization } = values;
      const finalValues = {
        ...values,
        locations: selectedLocations,
        author,
        orgId: organization?.value,
        orgName: organization?.label,
      };

      try {
        setBtnLoading(true);
        const res = await addDocumentReference(finalValues);
        if (res.status === true) {
          success(res.message);
          handleShow();
          refreshTable();
        } else {
          failed(res.error);
        }
      } catch (err) {
        failed(err?.response?.data?.message || err?.response?.data?.error || err.message);
      } finally {
        setBtnLoading(false);
      }
    },
  });

  // --- NEW: derive selected org and fetch locations for it
  const selectedOrgId = formik.values.organization?.value || null;

  const { data: orgLocations = [], isFetching: locLoading } = useClinicLocationsQuery({
    orgIds: selectedOrgId ? [selectedOrgId] : [],
    enabled: !!selectedOrgId, // don’t fetch until an org is chosen
    onSuccess: (data) => {
      // normalize into Select options shape
      setLocations(
        (data || []).map((l) => ({
          value: l?.id,
          label: l?.name || "Unnamed Location",
        }))
      );
      // if org changed, clear any previously selected locations not in this org
      formik.setFieldValue("locations", []);
    },
  });

  useEffect(() => {
    if (modalShow) formik.resetForm();
  }, [modalShow]);

  return (
    <Modal
      backdrop={"static"}
      size="lg"
      show={modalShow}
      onHide={handleShow}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      className="custom-dialog"
    >
      <Modal.Header closeButton className="border-0">
        <Modal.Title id="contained-modal-title-vcenter">Create Resource</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form className="common-form border-fields" onSubmit={formik.handleSubmit}>
          <Row>
            <Col className="field-wrap select">
              <label id="mutiple-select-label"> Select Organization</label>
              <Select
                options={organizations.current.map((org) => ({
                  value: org.id,
                  label: org.name || "Unnamed Organization",
                }))}
                value={formik.values.organization}
                onChange={(selected) => {
                  formik.setFieldValue("organization", selected || null);
                  // clearing locations happens in onSuccess of query after fetch
                }}
                placeholder="Select organization"
              />
            </Col>

            <Col>
              <TextInput keyField={"name"} label={"Name"} formik={formik} placeholder={"Document Name"} />
            </Col>
          </Row>

          <Row>
            <Col>
              <TextArea keyField={"description"} label={"Description"} formik={formik} placeholder={"Description..."} />
            </Col>
          </Row>

          <Row>
            <Col>
              <TextInput keyField={"url"} label={"URL"} formik={formik} placeholder={"URL"} />
            </Col>
          </Row>

          <Row>
            <Col className="field-wrap multiselect">
              <label id="mutiple-select-label"> Select Clinical Locations</label>
              <Select
                isDisabled={!selectedOrgId || locLoading}
                isLoading={locLoading}
                options={locations}
                isMulti
                value={formik.values.locations}
                onChange={(selected) => formik.setFieldValue("locations", selected || [])}
                placeholder={selectedOrgId ? "Select locations" : "Pick an organization first"}
                noOptionsMessage={() => selectedOrgId ? "No locations found" : "Pick an organization first"}
              />
            </Col>
          </Row>

          <div className="btn-wrap" style={{ display: "flex" }}>
            <Button onClick={() => handleShow(false)} variant="secondary" title="Cancel">Cancel</Button>
            <Button type="submit" isLoading={btnLoading}>Create</Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}